/**
 * Step 5 In funnel
 * View and print report
 *
 * Route should be: /report/:fpzReportId?
 * Example:
 *   <Route path="/report/:fpzReportId?" element={<ReportPage />} />
 */
import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useDeepCompareEffect } from 'react-use';
import {
  Box, Tabs, Tab, Card, CssBaseline, Toolbar,
  Button, ButtonGroup, Menu, MenuItem,
  Grid, Typography, TextField, CircularProgress,
  DialogTitle, Dialog, DialogContent, DialogActions, DialogContentText,
} from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';

import Header from '../components/Header';
import Cube from '../components/Cube';
import FpzTable from '../components/FpzTable';
import FpzTableSummaries from '../components/FpzTable/summaries';
import ContactsTableDisplay from '../components/ContactsTable/display';

import useCartofactWells from '../hooks/useCartofactWells';
import { fpzActions, emailActions } from '../actions';

const ReportPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const authState = useSelector(state => state.authReducer);
  const fpzState = useSelector(state => state.fpzReducer);
  const userState = useSelector(state => state.userReducer);

  const [printAnchorEl, setPrintAnchorEl] = useState(null);
  const [accountAnchorEl, setAccountAnchorEl] = useState(null);

  const [open, setOpen] = React.useState(false);
  const [dialogTitle, setDialogTitle] = useState('');
  const [dialogContent, setDialogContent] = useState('');
  const [dialogContentText, setDialogContentText] = useState('');
  const [dialogActions, setDialogActions] = useState(null);

  // save
  const saveReportRef = useRef({
    report_name: fpzState?.report_name || '',
    report_description: fpzState?.report_description || '',
  });

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = (value) => {
    setOpen(false);
  };

  const handleMenuClick = (event, anchor) => {
    switch (anchor) {
      case 'print':
        setPrintAnchorEl(event.currentTarget);
        break;
      case 'account':
        setAccountAnchorEl(event.currentTarget);
        break;
      default:
        console.error('Unknown menu anchor:', anchor);
    }
  };

  const handleMenuClose = (action) => {
    setPrintAnchorEl(null);
    setAccountAnchorEl(null);
    if (action) action();
  };

  const handlePrintReport = () => {
    // check that there is at least one verified offset well
    
    console.log('/print');
    window.print();
  };

  const handleExport = () => {
    console.log('handleExport');
  };

  const handleSaveForm = () => {
    console.log('handleSaveForm', saveReportRef.current);
    let fpzReportId = fpzState?.fpz_report_id || null;

    setDialogTitle('Save Report');
    setDialogContentText('Save your report to the FPZ database.');
    setDialogContent(
      <>
        <TextField
          autoFocus
          margin="dense"
          label="Report Name"
          type="text"
          fullWidth
          variant="outlined"
          onChange={(e) => handleChangeSaveForm('report_name', e.target.value)}
          defaultValue={saveReportRef?.current?.report_name || ''}
        />
        <TextField
          margin="dense"
          label="Report Description"
          fullWidth
          variant="outlined"
          multiline
          rows={4}
          maxRows={4}
          onChange={(e) => handleChangeSaveForm('report_description', e.target.value)}
          defaultValue={saveReportRef?.current?.report_description || ''}
        />
      </>
    );
    setDialogActions(
      <>
        <Button onClick={handleSaveReport}>Save</Button>
      </>
    );
    handleClickOpen();
  };

  const handleShare = (emailRecipient =  null) => {
    console.log('handleShare', saveReportRef.current);
    let fpzReportId = fpzState?.fpz_report_id || null;

    handleChangeSaveForm('email_recipient', emailRecipient);
    setDialogTitle('Send Report to Abra');
    setDialogContentText('The report will be saved to your account and sent to Abra.');
    setDialogContent(
      <>
        <TextField
          autoFocus
          margin="dense"
          label="Report Name"
          type="text"
          fullWidth
          variant="outlined"
          onChange={(e) => handleChangeSaveForm('report_name', e.target.value)}
          defaultValue={saveReportRef?.current?.report_name || ''}
        />
        <TextField
          margin="dense"
          label="Report Description"
          fullWidth
          variant="outlined"
          multiline
          rows={4}
          maxRows={4}
          onChange={(e) => handleChangeSaveForm('report_description', e.target.value)}
          defaultValue={saveReportRef?.current?.report_description || ''}
        />
      </>
    );
    setDialogActions(
      <>
        <Button onClick={() => handleSaveReport({ isShare: true })}>Send</Button>
      </>
    );
    handleClickOpen();
  };

  const handleChangeSaveForm = (key, value) => {
    saveReportRef.current = {
      ...saveReportRef.current,
      [key]: value.trim(),
    };
    console.log('handleChangeSaveForm', key, value, saveReportRef.current);
  }
  
  const handleSaveReport = async ({ isShare = false } = {}) => {
    setDialogActions(<CircularProgress />);

    let fpzReportId = fpzState?.fpz_report_id || null;
    const params = {
      ...saveReportRef.current,
      user_id: authState?.profile?.userId,
      report_details: {
        boundingBox: fpzState?.boundingBox,
        map: fpzState?.map,
        selectedTreatmentWells: fpzState?.selectedTreatmentWells,
        filter: fpzState?.filter,
        contacts: fpzState?.contacts,
        reportImages: fpzState?.reportImages,
      },
    }
    console.log('handleSaveReport', params);
    let response = null;
    if (!fpzReportId) {
      response = await fpzActions.createFpzReport(params);
    } else {
      params.fpz_report_id = fpzReportId;
      response = await fpzActions.updateFpzReport(params);
    }
    console.log('handleSaveReport response', response);
    if (response?.success) {
      
      // Email report if isShare is true
      if (isShare) {
        // call backend to email report 
        const emailParams = {
          fpz_report_id: response.fpz_report_id,
          user_id: authState?.profile?.userId,
          email_recipient: saveReportRef?.current?.email_recipient,
          report_name: saveReportRef?.current?.report_name,
          report_description: saveReportRef?.current?.report_description,
          user_email: userState?.user?.user_email,
          user_first_name: userState?.user?.user_first_name,
          user_last_name: userState?.user?.user_last_name,
          sender_name: `${userState?.user?.user_first_name || ''} ${userState?.user?.user_last_name || ''}`.trim(),
          sender_email: userState?.user?.user_email,
          report_url: `${window.location.origin}/report/${response.fpz_report_id}`,
        };
        const emailResponse = await emailActions.sendEmail(emailParams);
        console.log('Email report response', emailResponse);
        //if (emailResponse?.success) {
          setDialogContentText('');
          setDialogContent('Report saved and emailed successfully.');
          setDialogActions(null);
        //}

      } else {

          // show response
        setDialogContentText('');
        setDialogContent('Report saved successfully.');
        setDialogActions(null);

      }

      

      setTimeout(() => {
        handleClose();
      }, 1500);
      
    } else {
      setDialogActions(
        <>
          <Button onClick={() => handleSaveReport({ isShare: true })}>Send</Button>
          <span style={{ color: 'red' }}>{response?.message || 'Error saving report'}</span>
        </>
      );
    }
    
  }

  // URL: /report/:fpzReportId
  const { fpzReportId } = useParams();
  const reportIdNum = useMemo(() => {
    const n = Number(fpzReportId);
    return Number.isFinite(n) ? n : null;
  }, [fpzReportId]);

  const {
    filter,
    selectedTreatmentWells = [],
    contacts: {
      operators = [],
      offsetOperators = [],
      otherContacts = [],
    } = {},
    reportImages: {
      cubeImage = '',
      mapImage = '',
    } = {},
    currentFpzReport, // optional: if you store current report in reducer

  } = useSelector((state) => state.fpzReducer);

  const isLoading = fpzReportId && (!Array.isArray(selectedTreatmentWells) || selectedTreatmentWells.length === 0);
  console.log('isLoading report:', isLoading, fpzReportId, selectedTreatmentWells);

  // If you need cartofact wells on this screen
  useCartofactWells({
    toggleCartofactWells: true,
    boundingBox: fpzState?.map?.boundingBox,
    filter,
  });

  const [cubeRender, setCubeRender] = useState(null);
  const [tabIndex, setTabIndex] = useState(0);
  const [selected, setSelected] = useState(0);

  const defaultFpzRadius = 500;

  /**
   * Load report when deep-linking:
   * - /report (no param): expects reducer already hydrated by prior steps
   * - /report/17: fetch report by id, dispatch into reducer
   *
   */
  useEffect(() => {
    const run = async () => {
      if (!reportIdNum) return;

      // avoid re-fetch if the reducer already has the report loaded
      const alreadyLoadedId = currentFpzReport?.fpz_report_id;
      if (alreadyLoadedId === reportIdNum) return;

      const fpzReport = await fpzActions.getFpzReport(reportIdNum);
      console.log('Deep-linked report fetched:', reportIdNum, fpzReport);

      dispatch({
        type: 'SET_CURRENT_FPZ_REPORT',
        payload: {
          ...fpzReport?.report_details,
          fpz_report_id: fpzReport?.fpz_report_id ?? null,
          report_name: fpzReport?.report_name ?? '',
          report_description: fpzReport?.report_description ?? '',
        },
      });
    };

    run();
  }, [reportIdNum, currentFpzReport?.fpz_report_id, dispatch]);

  /**
   * Guard: If user is not deep-linking AND no wells exist, go back to funnel
   * - deep link: let the fetch above populate state
   * - non-deep: current behavior
   */
  useEffect(() => {
    const hasParam = reportIdNum !== null;
    if (hasParam) return;

    if (!selectedTreatmentWells || selectedTreatmentWells.length === 0) {
      navigate('/treatment');
    }
  }, [reportIdNum, selectedTreatmentWells, navigate]);

  /**
   * Tabs
   */
  const handleTabChange = (event, newValue) => {
    if (newValue === 999) {
      setTabIndex(newValue);
      setSelected(null);
    } else {
      setTabIndex(newValue);
      setSelected(newValue);
    }
  };

  /**
   * Cube renderer (unchanged logic; tightened dependencies)
   */
  useDeepCompareEffect(() => {
    let directionalSurveys = [];

    if (selected !== null && selected !== undefined) {
      directionalSurveys = selectedTreatmentWells?.[selected]?.directionalSurveys || [];
    } else {
      directionalSurveys = (selectedTreatmentWells || [])
        .map((w) => w?.directionalSurveys || [])
        .flat();
    }

    if (directionalSurveys?.length > 0) {
      setCubeRender(null);
      setTimeout(() => {
        setCubeRender(
          <Cube
            directionalSurveys={directionalSurveys}
            selectedTreatmentWell={selectedTreatmentWells?.[selected]}
            toggleFpzRadius={true}
            toggleOffsetWells={true}
          />
        );
      }, 500);
    } else {
      setCubeRender(null);
    }
  }, [
    selectedTreatmentWells,
    selected,
  ]);

  /**
   * Optional: If report is loaded but no wells exist after load, force redirect.
   * Keeps /report/17 from rendering broken content.
   */
  useEffect(() => {
    if (!reportIdNum) return;

    // Once report is loaded (or attempted), if still no wells, redirect
    // If you have a loading flag, use it instead of this heuristic.
    if (currentFpzReport?.fpz_report_id === reportIdNum) {
      if (!selectedTreatmentWells || selectedTreatmentWells.length === 0) {
        navigate('/treatment');
      }
    }
  }, [reportIdNum, currentFpzReport?.fpz_report_id, selectedTreatmentWells, navigate]);

  const showCombined = selectedTreatmentWells?.length > 1;

  if (!isLoading) {
    return (
      <Box>

        <div className="content">
          <Header
            title="Report"
          />

          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} md={6}>
              <h3 className="no-print" >
                Treatment Wells
              </h3>
            </Grid>
            <Grid item xs={12} md={6} className="no-print" sx={{ textAlign: 'right' }}>
              <ButtonGroup variant="contained" color="info">
                <Button
                  onClick={handlePrintReport}
                >
                  Print
                </Button>
                <Button
                  size="small"
                  onClick={(event) => { handleMenuClick(event, 'print') }}
                >
                  <ArrowDropDownIcon />
                </Button>
                <Menu
                  anchorEl={printAnchorEl}
                  open={Boolean(printAnchorEl)}
                  onClose={() => handleMenuClose()}
                >
                  <MenuItem onClick={() => handleMenuClose(handleSaveForm)}>Save</MenuItem>
                  { /*<MenuItem onClick={() => handleMenuClose(handleExport)}>Export</MenuItem>*/ }
                  <MenuItem onClick={() => handleMenuClose(handleShare('roger.rigsby@abracontrols.com'))}>Send to Abra</MenuItem>
                </Menu>
              </ButtonGroup>
            </Grid>
          </Grid>
              
          

          <Tabs
            value={tabIndex}
            onChange={handleTabChange}
            indicatorColor="primary"
            textColor="inherit"
            variant="scrollable"
            scrollButtons="auto"
            className="no-print"
          >
            {selectedTreatmentWells.map((well, index) => (
              <Tab key={index} label={well?.unique_identifier} />
            ))}
            {showCombined && (
              <Tab
                key={999}
                value={999}
                label="Combined*"
                title="View Combined Treatment and Offset Wells"
              />
            )}
          </Tabs>

          {/* PRINT ONLY COVER PAGE */}
          <div className="print-only" style={{ textAlign: 'center', paddingTop: '200px', width: '100%' }}>
            <h1>Treatment Wells</h1>
            <h2>{selectedTreatmentWells?.[0]?.operator}</h2>
            {selectedTreatmentWells.map((well, index) => (
              <div key={index}>{well?.unique_identifier}</div>
            ))}
          </div>

          <div className="footer"></div>

          {/* Main layout */}
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', p: 2 }}>
            {/* Print-only tabs */}
            <div className="print-only">
              <h3 style={{ marginLeft: '15px', marginBottom: 0 }}>Treatment Wells</h3>
              <Tabs
                value={tabIndex}
                onChange={handleTabChange}
                indicatorColor="primary"
                textColor="inherit"
                variant="scrollable"
                scrollButtons="auto"
              >
                {selectedTreatmentWells.map((well, index) => (
                  <Tab key={index} label={well?.unique_identifier} />
                ))}
                {showCombined && (
                  <Tab
                    key={999}
                    value={999}
                    label="Combined*"
                    title="View Combined Treatment and Offset Wells"
                  />
                )}
              </Tabs>
            </div>

            {/* Map and Cube */}
            <Box sx={{ flex: '1 1 50%', display: 'flex', gap: 2, mb: 2 }}>
              <Card id="map-card" sx={{ flex: 1, height: '100%' }}>
                <Box sx={{ position: 'relative', height: '100%' }}>
                  <img
                    src={mapImage}
                    alt="Captured Map"
                    style={{ width: '100%', border: '1px solid #ccc' }}
                  />
                </Box>
              </Card>

              <Card id="cube-card" sx={{ flex: 1, height: '100%' }}>
                <Box sx={{ position: 'relative', height: '100%' }}>
                  <img
                    src={cubeImage}
                    alt="Captured Cube"
                    style={{ width: '100%', border: '1px solid #ccc' }}
                  />
                </Box>
              </Card>
            </Box>

            <Box className="print-text" sx={{ flex: '0 0 auto', p: 2 }}>
              <h3>Contacts</h3>
              <Box>{operators.length > 0 && <ContactsTableDisplay initialData={operators} />}</Box>

              <h3>Offset Operators</h3>
              <Box>{offsetOperators.length > 0 && <ContactsTableDisplay initialData={offsetOperators} />}</Box>
            </Box>

            <Box className="print-text" sx={{ flex: '0 0 auto', p: 2 }}>
              <h3>Shared Offset Wells - FPZ</h3>
              <Box>
                <FpzTable selected={selected} isReport={true} />
              </Box>
            </Box>

            <Box className="print-text" sx={{ flex: '0 0 auto', p: 2 }}>
              <h3>Summaries</h3>
              <Box>
                <FpzTableSummaries selected={selected} isReport={true} />
              </Box>
            </Box>
          </Box>
        </div>

        {/* Footer */}
        <div className="print-footer">
          Abra Controls Directive 83 Report – © {new Date().getFullYear()} Spottertech
        </div>

        <Dialog open={open} onClose={handleClose}>
          <DialogTitle>{dialogTitle}</DialogTitle>
          <DialogContent>
            <DialogContentText>{dialogContentText}</DialogContentText>
            {dialogContent}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose}>Cancel</Button>
            {dialogActions}
          </DialogActions>
        </Dialog>
      </Box>
    );
  }
};

export default ReportPage;
